import type { ITelemetryItem } from '@microsoft/applicationinsights-web';

import { maskUrlSecrets } from '@/shared/helpers/mask-url-secrets';

/** Separates browser telemetry from the server's (`web-server`) in the Application Map and in queries. */
export const BROWSER_CLOUD_ROLE = 'web-browser';

/**
 * The query parameters that pick which page someone is on. Project detail is `/projects?projectId=…`
 * (see `buildProjectDetailPath`), so a path alone is not enough; everything else in the query is list
 * or UI state (`limit`, `offset`, filters, sort) that changes without the user going anywhere.
 */
const PAGE_IDENTITY_PARAMS = ['projectId', 'designId', 'version', 'tab', 'view'] as const;

/** The path plus the parameters that identify a page; two URLs with the same identity are one page. */
export function pageIdentity(uri: string): string {
  const url = new URL(uri, 'http://identity.invalid');
  const identity = new URLSearchParams();
  for (const name of PAGE_IDENTITY_PARAMS) {
    const value = url.searchParams.get(name);
    if (value !== null) identity.set(name, value);
  }
  const query = identity.toString();
  return query ? `${url.pathname}?${query}` : url.pathname;
}

/**
 * A telemetry initializer that drops a page view when the page has not changed. Auto route tracking
 * records one on every `history.replaceState`, and the projects list rewrites its pagination into the
 * URL on every fetch, so page counts and time-on-page were inflated by views of the same page.
 */
export function createRepeatPageViewFilter() {
  let lastIdentity: string | undefined;

  return (item: ITelemetryItem): boolean | void => {
    if (item.baseType !== 'PageviewData') return;
    const uri = (item.baseData as { uri?: string } | undefined)?.uri;
    if (!uri) return;

    const identity = pageIdentity(uri);
    if (identity === lastIdentity) return false;
    lastIdentity = identity;
  };
}

/** Tags every item with the browser's cloud role and the build that produced it. */
export function tagBrowserTelemetry(buildId: string | undefined) {
  return (item: ITelemetryItem): void => {
    item.tags = item.tags ?? {};
    item.tags['ai.cloud.role'] = BROWSER_CLOUD_ROLE;
    if (item.baseData) {
      item.baseData.properties = { ...item.baseData.properties, buildId: buildId || 'unknown' };
    }
  };
}

/**
 * Masks credentials in the URLs the browser SDK records: dependency `name`/`target` for fetch and XHR
 * calls carrying a Mapbox or Google key, and page-view `uri`s carrying a magic-link token.
 */
export function maskBrowserTelemetryUrls(item: ITelemetryItem): void {
  const baseData = item.baseData as Record<string, unknown> | undefined;
  if (!baseData) return;
  for (const [key, value] of Object.entries(baseData)) {
    if (typeof value === 'string') baseData[key] = maskUrlSecrets(value);
  }
}
